"use client";

import { IndianRupee, ShieldCheck, Target, Users } from "lucide-react";
import {
  AnimatedCounter,
  ImpactStatCard,
  Reveal,
  Stagger,
  StaggerItem,
} from "@/components/motion";
import type { Stats } from "@/lib/api-types";
import { site } from "@/content/site";

interface DonateStatsStripProps {
  stats: Stats | null;
  statsLoading: boolean;
  statsError?: boolean;
}

function StatsSkeleton() {
  return (
    <div
      className="mx-auto grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3"
      aria-hidden
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="donate-stat-card h-28 animate-pulse rounded-xl"
        />
      ))}
    </div>
  );
}

export function DonateStatsStrip({
  stats,
  statsLoading,
  statsError = false,
}: DonateStatsStripProps) {
  return (
    <section className="donate-stats-strip bg-[var(--charity-surface-alt)] px-4 py-10 md:py-12">
      <div className="mx-auto max-w-4xl">
        <Reveal>
          {statsLoading ? (
            <StatsSkeleton />
          ) : stats ? (
              <Stagger className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <StaggerItem>
                  <div className="donate-stat-card rounded-xl px-4 py-4">
                    <ImpactStatCard
                      iconVariant="brand"
                      fallbackIcon={IndianRupee}
                      label="Raised This Year"
                      valueClassName="text-[var(--charity-green-dark)]"
                      displayValue={
                        <>
                          ₹
                          <AnimatedCounter
                            value={stats.raisedThisYear}
                            formatter={(n) =>
                              new Intl.NumberFormat("en-IN", {
                                maximumFractionDigits: 0,
                              }).format(n)
                            }
                          />
                          +
                        </>
                      }
                    />
                  </div>
                </StaggerItem>
                <StaggerItem>
                  <div className="donate-stat-card rounded-xl px-4 py-4">
                    <ImpactStatCard
                      iconVariant="brand"
                      fallbackIcon={Users}
                      label="Lives Impacted"
                      valueClassName="text-[var(--charity-green-dark)]"
                      value={Math.round(stats.livesImpacted / 1000)}
                      suffix="K+"
                    />
                  </div>
                </StaggerItem>
                <StaggerItem>
                  <div className="donate-stat-card rounded-xl px-4 py-4">
                    <ImpactStatCard
                      iconVariant="brand"
                      fallbackIcon={Target}
                      label="Direct to Programs"
                      valueClassName="text-[var(--charity-green-dark)]"
                      value={stats.directToProgramsPercent ?? 95}
                      suffix="%"
                    />
                  </div>
                </StaggerItem>
              </Stagger>
          ) : statsError ? (
            <p className="text-center text-sm text-muted">
              Impact stats temporarily unavailable.
            </p>
          ) : null}
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted md:text-sm">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck
                size={14}
                className="text-[var(--charity-green)]"
                aria-hidden
              />
              {site.donate.taxBenefitNote}
            </span>
            <span className="hidden text-border sm:inline" aria-hidden>
              ·
            </span>
            <span>Secure payment via Razorpay</span>
            <span className="hidden text-border sm:inline" aria-hidden>
              ·
            </span>
            <span>Receipt emailed after payment</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
